// client/src/graphql/pimcore/modules/contacts/contexts/RepresentativeProvider.tsx
import { createContext, ReactNode, useContext, useMemo, useState, useEffect } from 'react';
import { ApolloClient, useQuery } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import { Representative } from '../types/Representative';
import { useGeographicRegions } from '../../geographic/contexts/GeographicRegionsProvider';
import {
    GET_REPRESENTATIVES_QUERY,
    getRepresentativesQueryVariables,
} from '../queries/getRepresentativeQuery';
import { transformRepresentativeResponse } from '../tools/transformRepresentativeResponse';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

interface RepresentativesContextValue {
    representatives: Representative[];
    representativeById: Record<string, Representative>;
    loading: boolean;
    error: any;
    setSelectedRepresentativeGeographicRegionId: (id: string[] | null) => void;
    selectedRepresentativeCountryId: string[] | null;
    setSelectedRepresentativeCountryId: (id: string[] | null) => void;
    filteredRepresentatives: Representative[];
}

const RepresentativesContext = createContext<RepresentativesContextValue | undefined>(undefined);

interface RepresentativesProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const RepresentativesProvider = ({ children, apolloClient }: RepresentativesProviderProps) => {
    const { locale } = useLocale();
    const { geographicRegionById } = useGeographicRegions();
    const [selectedRepresentativeGeographicRegionId, setSelectedRepresentativeGeographicRegionId] =
        useState<string[] | null>(null);
    const [selectedRepresentativeCountryId, setSelectedRepresentativeCountryId] = useState<
        string[] | null
    >(null);

    const query = GET_REPRESENTATIVES_QUERY;
    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery(query, {
        variables: getRepresentativesQueryVariables(locale),
        client: apolloClient,
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, locale, initialized: true, queryError: queryError });

    useEffect(() => {
        if (queryError) {
            console.error('RepresentativesProvider query error', queryError);
        }
    }, [queryError]);

    const representatives = useMemo(() => {
        const source = queryData ?? fallbackData;
        if (!source) return [];
        return transformRepresentativeResponse(source);
    }, [queryData, fallbackData]);

    const representativeById = useMemo(() => {
        const map: Record<string, Representative> = {};
        representatives.forEach((o) => {
            map[o.id] = o;
        });
        return map;
    }, [representatives]);

    const filteredRepresentatives = useMemo(() => {
        let result = representatives;

        if (
            selectedRepresentativeGeographicRegionId &&
            selectedRepresentativeGeographicRegionId.length > 0
        ) {
            const regionCountryIds = selectedRepresentativeGeographicRegionId.flatMap(
                (regionId) => {
                    const region = geographicRegionById[regionId];
                    return region ? region.countries.map((c) => c.id) : [];
                }
            );
            result = result.filter((o) =>
                regionCountryIds.some((id) => o.countryOrSubnational.includes(id))
            );
        }

        if (selectedRepresentativeCountryId && selectedRepresentativeCountryId.length > 0) {
            result = result.filter((o) =>
                selectedRepresentativeCountryId.some((id) => o.countryOrSubnational.includes(id))
            );
        }

        return result;
    }, [
        representatives,
        selectedRepresentativeGeographicRegionId,
        selectedRepresentativeCountryId,
        geographicRegionById,
    ]);

    const loading = loadingQuery || loadingFallback;
    // if we have fallback data, we don't consider it an error
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <RepresentativesContext.Provider
            value={{
                representatives,
                representativeById,
                loading,
                error,
                setSelectedRepresentativeGeographicRegionId,
                selectedRepresentativeCountryId,
                setSelectedRepresentativeCountryId,
                filteredRepresentatives,
            }}
        >
            {children}
        </RepresentativesContext.Provider>
    );
};

function useRepresentatives() {
    const context = useContext(RepresentativesContext);
    if (!context) {
        throw new Error('useRepresentatives must be used within a RepresentativesProvider');
    }
    return context;
}

export { RepresentativesProvider, useRepresentatives };
export type { RepresentativesContextValue };
